'use client';

import Link from 'next/link';
import { isRecitationMeeting } from '@/app/lib/meetingTypes';
import { ChevronRightIcon, ClockIcon, MapPinIcon, StarIcon } from '@heroicons/react/24/outline';

import { findProfessorRatings } from '@/app/lib/professorData';
import { formatBaseIdDisplay } from '@/app/lib/utils';
import type { ScheduledCourse } from '@/app/types/course';
import { getCampusColor } from '@/app/types/colors';
import { readableCourseTitle } from '@/app/lib/courseTitle';

function formatTime(minutes: number) {
  const hour = Math.floor(minutes / 60);
  const minute = minutes % 60;
  const period = hour >= 12 ? 'PM' : 'AM';
  const displayHour = hour % 12 || 12;
  return `${displayHour}:${String(minute).padStart(2, '0')} ${period}`;
}

function instructors(course: ScheduledCourse) {
  return course.instructors
    .map((instructor) => typeof instructor === 'string' ? instructor : instructor.name)
    .filter((name) => Boolean(name) && name !== 'TBA');
}

export default function ScheduleCourseList({
  courses,
  onOpenCourse,
}: {
  courses: ScheduledCourse[];
  onOpenCourse: (course: ScheduledCourse) => void;
}) {
  const totalCredits = courses.reduce((total, course) => {
    const value = typeof course.credits === 'string' ? Number.parseFloat(course.credits) : course.credits;
    return total + (value || 0);
  }, 0);

  return (
    <section className="space-y-4" aria-label="Scheduled courses list">
      <p className="text-sm text-[var(--ss-text-muted)]">{courses.length} courses · {totalCredits} credits</p>

      {courses.length ? (
        <div className="grid gap-3 lg:grid-cols-2">
          {courses.map((course, index) => {
            const professorNames = instructors(course);
            const professorEvidence = professorNames.map((name) => ({
              name,
              rating: findProfessorRatings([name])[0],
            }));
            return (
              <article key={`${course.id}-${index}`} className="group overflow-hidden rounded-card border border-[var(--ss-border)] bg-[var(--ss-surface)] transition-colors hover:border-[var(--ss-focus)] focus-within:ring-2 focus-within:ring-[var(--ss-focus)] motion-reduce:transition-none">
                <div className={`h-1.5 ${getCampusColor(course.campus, 'solid')}`} aria-hidden="true" />
                <button type="button" onClick={() => onOpenCourse(course)} className="block w-full p-4 pb-3 text-left focus-visible:outline-none" aria-label={`Open information for ${course.name}`}>
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <p className="font-semibold text-[var(--ss-text)]">{readableCourseTitle(course.name)}</p>
                      <p className="mt-1 text-xs text-[var(--ss-text-muted)]">{formatBaseIdDisplay(course.baseId)} · Section {course.section}</p>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className={`rounded-control px-2 py-1 text-[10px] font-semibold ${getCampusColor(course.campus, 'badge')}`}>{course.campus}</span>
                      <ChevronRightIcon className="h-5 w-5 shrink-0 text-[var(--ss-text-muted)] transition-colors group-hover:text-[var(--ss-focus)] motion-reduce:transition-none" />
                    </div>
                  </div>
                  <div className="mt-3 grid gap-2 text-xs text-[var(--ss-text-muted)]">
                    {course.meetingTimes.map((meeting, meetingIndex) => (
                      <div key={`${meeting.day}-${meeting.startMinute}-${meetingIndex}`} className="flex flex-wrap items-center gap-x-3 gap-y-1">
                        <span className="inline-flex items-center gap-1">{isRecitationMeeting(meeting) ? <span title="Recitation" aria-label="Recitation" className="rounded bg-[var(--ss-surface-subtle)] px-1 font-bold text-content">R</span> : <ClockIcon className="h-4 w-4" />}{meeting.day} {formatTime(meeting.startMinute)}–{formatTime(meeting.endMinute)}</span>
                        <span className="inline-flex items-center gap-1"><MapPinIcon className="h-4 w-4" />{meeting.location || meeting.campus || course.campus}</span>
                      </div>
                    ))}
                  </div>
                </button>
                <div className="flex flex-wrap gap-2 border-t border-[var(--ss-border)] px-4 py-3">
                  {professorEvidence.length ? professorEvidence.map(({ name, rating }) => (
                      <Link key={name} href={`/sirs?professor=${encodeURIComponent(name)}`} className="inline-flex min-h-9 items-center gap-1.5 rounded-control px-2 text-sm font-medium text-[var(--ss-text-muted)] transition-colors hover:bg-[var(--ss-surface-subtle)] hover:text-[var(--ss-focus)] hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--ss-focus)] motion-reduce:transition-none" title={`Open professor profile for ${name}`}>
                        <span>{name}</span>
                        {rating ? <span className="inline-flex items-center gap-1 font-semibold tabular-nums text-[var(--ss-text)]"><StarIcon className="h-3.5 w-3.5" />{rating.avgRating.toFixed(1)} RMP</span> : null}
                      </Link>
                  )) : <span className="text-sm text-[var(--ss-text-muted)]">Instructor TBA</span>}
                </div>
              </article>
            );
          })}
        </div>
      ) : (
        <div className="rounded-card border border-dashed border-[var(--ss-border)] p-8 text-center text-sm text-[var(--ss-text-muted)]">Add courses to see the schedule as a list.</div>
      )}
    </section>
  );
}
